import React, { useEffect, useRef, useState } from "react";
import { ChromePicker } from "react-color";
import { useTranslation } from "react-i18next";
import { createStyles, palette } from "../../../../styles/theme";
import { Box } from "../../Box";
import { Menu } from "@mui/material";
import { Icon } from "../../Icon";
import useEyeDropper from "use-eye-dropper";

const styles = createStyles({
  container: {
    width: "100%",
    "& .chrome-picker": {
      width: "100%",
    },
  },
  headerContainer: {
    display: "flex",
    backgroundColor: "#eeeeee",
    borderBottom: "2px solid #dddddd",
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
  },
  headerButton: {
    py: "4px",
    px: "8px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#dddddd",
    },
  },
  closeButton: {
    flex: 1,
    borderTopRightRadius: "8px",
  },
  eyeDropperButton: {
    borderTopLeftRadius: "8px",
    minWidth: "40px",
  },
});

interface Props {
  value?: string;
  isOpen: boolean;
  onColorChange: (values: string) => void;
  onClose?: () => void;
  anchorEl?: HTMLElement | null;
  width?: string;
}

// Utility function to detect Brave browser
const isBraveBrowser = () => {
  // Check for Brave-specific properties
  // @ts-ignore - navigator.brave is specific to Brave
  if (typeof navigator.brave !== "undefined") {
    return true;
  }

  // Fallback: Check user agent for Brave signature
  return /Brave/.test(navigator.userAgent);
};

// Hook to combine library functionality with Brave detection
const useBraveAwareEyeDropper = () => {
  const { open, close, isSupported } = useEyeDropper();
  const [isBrave, setIsBrave] = useState(false);
  const [isPickingColor, setIsPickingColor] = useState(false);

  useEffect(() => {
    const brave = isBraveBrowser();
    setIsBrave(brave);
  }, []);

  const openWithTracking = async () => {
    setIsPickingColor(true);
    try {
      const result = await open();
      return result;
    } finally {
      setIsPickingColor(false);
    }
  };

  const closeWithTracking = () => {
    setIsPickingColor(false);
    close();
  };

  useEffect(() => {
    if (!isPickingColor) {
      // Reset state when not picking
      setIsPickingColor(false);
    }
  }, [isPickingColor]);

  return {
    open: openWithTracking,
    close: closeWithTracking,
    isSupported,
    isPickingColor,
    isBrave,
  };
};

const ColorPicker = ({
  value,
  isOpen,
  onColorChange,
  onClose,
  anchorEl,
  width,
}: Props) => {
  const { t } = useTranslation();
  const { open, close, isSupported, isPickingColor, isBrave } =
    useBraveAwareEyeDropper();
  const [showBraveHelp, setShowBraveHelp] = useState(false);
  const buttonRef = useRef<HTMLElement>(null);

  const handleEyeDropper = () => {
    if (!isSupported) {
      const message = isBrave
        ? t(
            "color_picker.eye_dropper.not_supported_brave",
            "Eye dropper is not supported in Brave browser due to privacy settings. Try disabling Brave Shields for this site or use Chrome/Edge."
          )
        : t(
            "color_picker.eye_dropper.not_supported",
            "Eye dropper is not supported in this browser"
          );
      alert(message);
      setShowBraveHelp(true);
      return;
    }

    // Ensure button has focus for user activation
    if (buttonRef.current) {
      buttonRef.current.focus();
    }

    open()
      .then((result: { sRGBHex: string }) => {
        onColorChange(result.sRGBHex);
      })
      .catch((error: any) => {
        if (error?.name !== "AbortError" && error?.name !== "NotAllowedError") {
          console.error("Eye dropper failed:", error);

          // Provide Brave-specific guidance
          if (isBrave) {
            setShowBraveHelp(true);
            alert(
              t(
                "color_picker.eye_dropper.not_supported_brave_failed",
                "Eye dropper failed in Brave. Try:\n1. Disable Brave Shields for this site\n2. Use a different browser (Chrome/Edge)\n3. Use the color input below as alternative"
              )
            );
          } else {
            alert(
              t(
                "color_picker.eye_dropper.not_supported_failed",
                "Eye dropper failed. Please try again."
              )
            );
          }
        }
      });
  };

  useEffect(() => {
    if (!isOpen && isPickingColor) {
      close();
    }
  }, [isOpen, isPickingColor, close]);

  return (
    isOpen && (
      <Menu
        open={isOpen}
        onClose={onClose}
        anchorEl={anchorEl}
        disablePortal
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        variant="menu"
        sx={{
          zIndex: 2000,
          "& .MuiMenu-paper": {
            width: width || `min(${anchorEl?.clientWidth}px, 300px)`,
            background: "transparent",
            boxShadow: "none",
          },
          "& .MuiList-root": {
            padding: "0px",
          },
        }}
      >
        <Box sx={styles.container}>
          <Box
            sx={{
              border: `1px solid ${palette.grey.darkGrey}`,
              borderRadius: "8px",
            }}
          >
            <Box sx={styles.headerContainer}>
              <Box
                ref={buttonRef}
                sx={[
                  styles.headerButton,
                  styles.eyeDropperButton,
                  {
                    opacity: isPickingColor ? 0.7 : 1,
                    cursor: isPickingColor ? "wait" : "pointer",
                    pointerEvents: isPickingColor ? "none" : "auto",
                  },
                ]}
                onClick={!isPickingColor ? handleEyeDropper : undefined}
                title={
                  isPickingColor
                    ? t(
                        "general.eyeDropperActive",
                        "Click on the screen to pick a color"
                      )
                    : !isSupported
                      ? t(
                          "general.eyeDropperNotSupported",
                          "Eye dropper is not supported in this browser"
                        )
                      : isBrave
                        ? t(
                            "general.eyeDropperBrave",
                            "Pick color from screen (may not work in Brave due to privacy settings)"
                          )
                        : t("general.eyeDropper", "Pick color from screen")
                }
                tabIndex={0}
              >
                <Icon
                  icon="colorize"
                  size={16}
                  color={
                    isPickingColor ? palette.blue.primary : palette.grey.primary
                  }
                />
                {(!isSupported || isBrave) && (
                  <Icon
                    icon="warning"
                    size={12}
                    color="#ff9800"
                    style={{ marginLeft: "2px" }}
                  />
                )}
              </Box>
              <Box
                sx={[styles.headerButton, styles.closeButton]}
                onClick={onClose}
              >
                {t(`general.close`)}
              </Box>
            </Box>
            <ChromePicker
              styles={{
                default: {
                  picker: {
                    width: "100%",
                    boxShadow: "none",
                    borderRadius:
                      !isSupported || isBrave || showBraveHelp
                        ? "8px 8px 0 0"
                        : "8px",
                  },
                },
              }}
              color={value}
              onChangeComplete={(color) => onColorChange(color.hex)}
              disableAlpha={true}
            />

            {/* Fallback color input for browsers with EyeDropper issues */}
            {(!isSupported || isBrave || showBraveHelp) && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderTop: `1px solid ${palette.grey.lightGrey}`,
                  backgroundColor: "#f9f9f9",
                  borderRadius: "0 0 8px 8px",
                }}
              >
                <Box
                  sx={{
                    fontSize: "11px",
                    color: palette.grey.primary,
                  }}
                >
                  {!isSupported
                    ? t(
                        "color_picker.eye_dropper.not_supported_notice",
                        "Eye dropper is not supported in this browser. Use the color picker above instead."
                      )
                    : isBrave
                      ? t(
                          "color_picker.eye_dropper.brave_notice",
                          "Brave browser's privacy features may interfere with the eyedropper tool."
                        )
                      : t(
                          "color_picker.eye_dropper.alternative_notice",
                          "Use this as an alternative if the eyedropper doesn't work."
                        )}
                </Box>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              height: 80,
              background: "transparent",
            }}
          />
        </Box>
      </Menu>
    )
  );
};

export default ColorPicker;
